import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { DialogMessage, HistoryItemResponse } from '@shared/index';
import { msgPromptSchema, msgTextSchema, msgWebsearchSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { PostType } from './iframe-communication/botapi-manager.service';
import { KeyValService } from './key-val.service';

/**
 * Steuert den Nachrichtenaustausch zwischen Nutzer und Bot (`RAGAI.MSG`).
 * @remarks
 * Zwei Achsen bestimmen das Verhalten jeder Methode:
 * - **Sichtbarkeit**: `prompt()`/`userMessage()`/`botMessage()` erscheinen im Chatverlauf; `userBypass()`/`botBypass()`
 *   landen unsichtbar im Thread-Kontext (die KI "sieht" sie, der Nutzer nicht) - nützlich, um der KI zusätzlichen
 *   Kontext mitzugeben, ohne den sichtbaren Chat zu verändern.
 * - **Run-Auslösung**: Nur `prompt()` löst einen vollständigen KI-Antwort-Run aus. Alle anderen Methoden fügen
 *   Nachrichten nur zum Verlauf hinzu, ohne dass der Bot antwortet.
 *
 * `prompt()` und `userMessage()` laufen durch alle registrierten `RAGAI.BEFORE_SEND`-Hooks (siehe
 * `MsgProcessingService`), bevor die Nachricht tatsächlich verarbeitet wird - ein Hook kann den Text ändern
 * oder das Senden per `null`-Rückgabe komplett unterdrücken.
 */
@Injectable({
  providedIn: 'root'
})
export class MessageManagerService {
  /** Feuert mit dem Bot-Text, sobald eine Bot-Nachricht in den Chatverlauf eingefügt wird. */
  public onBotToUser: EventEmitter<string> = new EventEmitter<string>();
  /** Feuert mit dem Nutzer-Text, sobald eine Nutzer-Nachricht in den Chatverlauf eingefügt wird. */
  public onUserToBot: EventEmitter<string> = new EventEmitter<string>();
  /** Feuert mit dem Nutzer-Text, sobald `userMessage()` aufgerufen wird. */
  public userMessageEmitter: EventEmitter<string> = new EventEmitter<string>();
  /** Feuert, sobald ein durch `prompt()` ausgelöster KI-Antwort-Run abgeschlossen ist. */
  public runCompleteEmitter: EventEmitter<string> = new EventEmitter<string>();
  /** Feuert mit dem Prompt-Text, sobald `prompt()` einen KI-Antwort-Run auslöst. */
  public runRequestEmitter: EventEmitter<string> = new EventEmitter<string>();
  constructor(
    private keyvalService: KeyValService,
    private botId: BotidService,
    private http: HttpClient
  ) {

  }

  /**
   * Sendet einen Text als Nutzereingabe an den Bot und löst einen vollständigen Antwort-Run aus.
   * @param text Der zu sendende Text, oder ein Objekt gemäß `msgTextSchema`.
   */
  public prompt(text: string | z.input<typeof msgTextSchema>): void {

    const parsed = msgTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    text = "" + (text as string);
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.prompt", { text });

    this.runRequestEmitter.emit(text);
  }

  /**
   * Fügt eine sichtbare Nutzer-Nachricht zum Chatverlauf hinzu, ohne eine Bot-Antwort auszulösen.
   * @param text Der anzuzeigende Text, oder ein Objekt gemäß `msgTextSchema`.
   */
  public userMessage(text: string | z.input<typeof msgTextSchema>): void {
    const parsed = msgTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    text = "" + (text as string);
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.userMessage", { text });
    this.userMessageEmitter.emit(text);
  }

  /**
   * Sendet eine Nachricht direkt als Bot in den Thread, ohne einen KI-Antwort-Run auszulösen (Bypass-Modus).
   * @param text Der Nachrichtentext, oder ein Objekt gemäß `msgTextSchema`.
   * @returns Die gespeicherten Verlaufsnachrichten.
   */
  public async botBypass(text: string | z.input<typeof msgTextSchema>): Promise<HistoryItemResponse[]> {
    const parsed = msgTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.botBypass", { text });
    const backendPayload: DialogMessage = { content: [{ text: text as string, type: "text" }], role: 'assistant', bypass: true };
    const res = await this._addMessagesToThread([backendPayload]);
    if (res[0] != null) {
      window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.botBypassComplete", { text: res[0] });
    }
    return res;
  }

  /**
   * Sendet eine Nachricht direkt als Nutzer in den Thread, ohne einen KI-Antwort-Run auszulösen (Bypass-Modus).
   * @param text Der Nachrichtentext, oder ein Objekt gemäß `msgTextSchema`.
   * @returns Die gespeicherte Verlaufsnachricht.
   */
  public async userBypass(text: string | z.input<typeof msgTextSchema>): Promise<HistoryItemResponse> {
    const parsed = msgTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    text = "" + (text as string);

    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.userBypass", { text });

    const backendPayload: DialogMessage = { content: [{ text, type: "text" }], role: 'user', bypass: true };
    const res = await this._addMessagesToThread([backendPayload]);
    void window.RAGAI.BOTAPI.postMessage(PostType.userBypassComplete, [res]);
    if (res[0] != null) {
      window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.userBypassComplete", { text: res[0] });
      return res[0];
    }
    throw new Error("Failed to add message");
  }

  // NOTICE: SYSTEMPROMPT MSG HOLT DER SERVER SICH SERVERSEITIG
  // WENN OVERWRITE MIT LEER PROMPT GESETZT
  // TODO:  wir bräuchten einen KEYVAL DELETE um z.b. hier den systemprompt einfach zu löschen
  /**
   * Überschreibt den Systemprompt des Bots für die aktuelle Sitzung.
   * @param prompt Der neue Systemprompt, oder ein Objekt gemäß `msgPromptSchema`.
   * @returns Nichts.
   */
  public async overwriteSystemPrompt(prompt: string | z.input<typeof msgPromptSchema>): Promise<void> {
    const parsed = msgPromptSchema.safeParse(prompt);
    if (parsed.success && typeof prompt === 'object') {
      prompt = parsed.data.prompt;
    }
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.overwriteSystemPrompt", { prompt });
    await this.keyvalService.set("systemprompt", prompt, "messagemanager");
  }


  /**
   * Aktiviert oder deaktiviert die Websuche des Bots für die aktuelle Sitzung.
   * @param enabled `true` aktiviert die Websuche, oder ein Objekt gemäß `msgWebsearchSchema`.
   * @returns Nichts.
   */
  public async overwriteWebsearchEnabled(enabled: boolean | z.input<typeof msgWebsearchSchema>): Promise<void> {
    const parsed = msgWebsearchSchema.safeParse(enabled);
    if (parsed.success && typeof enabled === 'object') {
      enabled = parsed.data.enabled;
    }
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.overwriteWebsearchEnabled", { prompt });
    await this.keyvalService.set("websearchenabled", enabled, "messagemanager");
  }

  /**
   * @internal Fügt Nachrichten direkt dem Server-Thread hinzu (Low-Level-Baustein der Bypass-/Card-Funktionen).
   * @param messages Die hinzuzufügenden Nachrichten.
   * @returns Die gespeicherten Verlaufsnachrichten.
   */
  public async _addMessagesToThread(messages: DialogMessage[]) {
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG._addMessagesToThread", messages);

    const botId = this.botId.getBotId();
    const res = await firstValueFrom(this.http.post<HistoryItemResponse[]>('/api/toolbox/membot/user/createmessagesinthread/' + botId, { messages }));

    return res;
  }



  /**
   * Sendet eine Bot-Nachricht inklusive Sprachausgabe (bei Audio-/Avatar-Modus).
   * @param text Der Nachrichtentext, oder ein Objekt gemäß `msgTextSchema`.
   * @returns Die gespeicherte Verlaufsnachricht.
   */
  public async botMessage(text: string | z.input<typeof msgTextSchema>): Promise<HistoryItemResponse | undefined> {
    const parsed = msgTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    text = "" + (text as string);
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.botMessage", { text });

    if (window.location.pathname.includes("audio") || window.location.pathname.includes("avatar")) {
      await window.RAGAI.AUDIO.botSay(text, undefined);
    }
    const res = await this._addMessagesToThread([{
      role: 'assistant',
      content: [{ text, type: "text" }]
    }]);
    void window.RAGAI.BOTAPI.postMessage(PostType.botMessageComplete, text);
    return res[0];
  }

  /**
   * Fügt eine normale (nicht-bypass) Nutzer-Nachricht direkt in den Thread ein,
   * ohne dafür eine reguläre KI-Chat-Anfrage auszulösen (kein Aufruf von `prompt()`
   * oder `userMessage()`, welche den normalen Sende-Flow erneut anstoßen würden).
   * Wird z.B. vom Freitext-Quiz genutzt, um die vom Nutzer eingegebene Antwort
   * sichtbar im Verlauf zu protokollieren.
   * @param text Der zu protokollierende Nutzertext, oder ein Objekt gemäß `msgTextSchema`.
   * @returns Die gespeicherte Verlaufsnachricht.
   */
  public async userMessageDirect(text: string | z.input<typeof msgTextSchema>): Promise<HistoryItemResponse | undefined> {
    const parsed = msgTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    text = "" + (text as string);
    const res = await this._addMessagesToThread([{
      role: 'user',
      content: [{ text, type: "text" }]
    }]);
    return res[0];
  }
}
